import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ProductBreadcrumb,
  ProductHero,
  ProductGallery,
  QuickSpecsTabs,
  KeyFeatures,
  Applications,
  WhyChooseLiftile,
  QualitySafetyCommitment,
  DownloadBrochure,
  PremiumFAQ,
  RelatedProducts,
  ProductFinalCTA,
} from "../components/product";
import {
  ArrowUp,
  Award,
  BadgeCheck,
  Boxes,
  Building2,
  ClipboardCheck,
  Cog,
  Cpu,
  Disc,
  Factory,
  Flag,
  Frame,
  Gauge,
  HardHat,
  Home,
  Landmark,
  Package,
  Scissors,
  ShieldCheck,
  Sparkles,
  Target,
  Wrench,
  Zap,
} from "lucide-react";

/* =====================================================================
   BAR CUTTING MACHINE (LRC Series — LRC40 / LRC50) — dedicated product page.
   Composed entirely from /components/product/* shared primitives.
   Only product-specific data lives in this file.
   ===================================================================== */

const HERO_IMAGE = "/product-bar-cutting.jpg";

const HERO_SPECS = [
  { Icon: Scissors, value: "Up to 42 mm", label: "Max TMT Bar Diameter" },
  { Icon: Zap, value: "5–6.5 HP", label: "Motor Power" },
  { Icon: Package, value: "2.5 Ton/hour", label: "Production Capacity" },
  { Icon: Flag, value: "Made in India", label: "Engineered & Manufactured" },
];

const HERO_BADGES = [
  "Automatic Operation",
  "Heavy Duty Construction",
  "Premium Components",
  "Made in India",
];

const GALLERY = [
  {
    src: "/product-bar-cutting.jpg",
    caption:
      "LIFTILE LRC Series Bar Cutting Machine — high-speed precision rebar cutting",
  },
];

const QUICK_SPECS = [
  { label: "Cutting Diameter", value: "6–42 mm TMT" },
  { label: "Motor Power", value: "5–6.5 HP" },
  { label: "Motor Speed", value: "2860 RPM" },
  { label: "Production", value: "2.5 Ton/hour" },
];

const MODELS = {
  LRC40: {
    name: "LRC40",
    tagline: "High-speed 32 mm TMT cutter for mid-scale rebar operations",
    rows: [
      { label: "Bar Cutting Diameter", value: "6–32 mm TMT Bar" },
      { label: "Motor", value: "5 HP" },
      { label: "Motor Speed", value: "2860 RPM" },
      { label: "Production Capacity", value: "2.5 Ton/hour" },
      { label: "Motor Protection", value: "IP44 / IP54 / IP55" },
      { label: "Operation", value: "Automatic" },
    ],
  },
  LRC50: {
    name: "LRC50",
    tagline: "Heavy-duty 42 mm TMT flagship for high-volume infrastructure sites",
    rows: [
      { label: "Bar Cutting Diameter", value: "6–42 mm TMT Bar" },
      { label: "Motor", value: "6.5 HP" },
      { label: "Motor Speed", value: "2860 RPM" },
      { label: "Production Capacity", value: "2.5 Ton/hour" },
      { label: "Motor Protection", value: "IP44 / IP54 / IP55" },
      { label: "Operation", value: "Automatic" },
    ],
  },
};

const FEATURES = [
  {
    Icon: Frame,
    title: "Heavy Duty Steel Body",
    desc: "Precision-welded heavy steel body engineered for rigidity, vibration control and long service life on demanding construction sites.",
  },
  {
    Icon: Disc,
    title: "High Strength Cutting Blade",
    desc: "Hardened, wear-resistant cutting blade delivers clean, accurate cuts through TMT bars up to 42 mm diameter.",
  },
  {
    Icon: Cpu,
    title: "Automatic Operation",
    desc: "Fully automatic cutting cycle for high productivity, consistent output and reduced operator fatigue during long shifts.",
  },
  {
    Icon: Zap,
    title: "Powerful Copper Winding Motor",
    desc: "Premium industrial-grade copper winding motor delivers dependable torque, efficiency and long service life.",
  },
  {
    Icon: Gauge,
    title: "High Speed Cutting",
    desc: "2860 RPM motor delivers rapid cutting cycles — up to 2.5 tonnes per hour of rebar throughput on demanding sites.",
  },
  {
    Icon: Target,
    title: "Accurate Cutting Performance",
    desc: "Precision-engineered cutting mechanism holds tight tolerances for clean, straight and repeatable rebar cuts.",
  },
  {
    Icon: Wrench,
    title: "Low Maintenance",
    desc: "Simple mechanical design with easy access to service points keeps downtime and operating costs low.",
  },
  {
    Icon: Cog,
    title: "Heavy Duty Gear System",
    desc: "Sealed heavy-duty gear system built for continuous industrial duty cycles and reliable rebar cutting operations.",
  },
  {
    Icon: Sparkles,
    title: "Premium Powder Coated Finish",
    desc: "7-tank pre-treatment with durable powder coating for site-grade corrosion resistance and a professional look.",
  },
  {
    Icon: HardHat,
    title: "Industrial Grade Construction",
    desc: "Fabricated from heavy-gauge structural steel with premium branded components for continuous industrial duty.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected Before Dispatch",
    desc: "Every LRC Series machine is personally inspected and quality signed off before it leaves the factory.",
  },
  {
    Icon: BadgeCheck,
    title: "Tested Before Dispatch",
    desc: "Full trial-run testing on every machine — verifying motor, blade, cutting accuracy and safety systems.",
  },
];

const APPLICATIONS = [
  {
    Icon: Home,
    title: "Residential Construction",
    desc: "Efficient on-site rebar cutting for apartment complexes, villas and residential towers.",
  },
  {
    Icon: Building2,
    title: "Commercial Buildings",
    desc: "High-productivity rebar operations for offices, malls, hotels and mixed-use developments.",
  },
  {
    Icon: Factory,
    title: "Industrial Projects",
    desc: "Dependable rebar cutting for plants, factories and heavy industrial installations.",
  },
  {
    Icon: Landmark,
    title: "Infrastructure Projects",
    desc: "Trusted performance on metros, treatment plants, power plants and public infrastructure works.",
  },
  {
    Icon: Landmark,
    title: "Bridge Construction",
    desc: "Precision rebar cutting for bridge girders, decks, piers and structural steel elements.",
  },
  {
    Icon: Scissors,
    title: "Steel Fabrication",
    desc: "Continuous-duty performance for structural steel fabrication yards and rebar workshops.",
  },
  {
    Icon: Boxes,
    title: "Precast Industries",
    desc: "Reliable rebar cutting for precast plants, formwork factories and modular construction.",
  },
  {
    Icon: ArrowUp,
    title: "High Rise Construction",
    desc: "Consistent, high-volume rebar output for tall residential and commercial towers.",
  },
];

const TRUST_CARDS = [
  {
    Icon: HardHat,
    title: "Heavy Duty Construction",
    desc: "Precision-welded steel body built for demanding site conditions and continuous industrial duty.",
  },
  {
    Icon: Target,
    title: "High Cutting Accuracy",
    desc: "Precision cutting mechanism delivers consistent, clean cuts through every bar for quality rebar output.",
  },
  {
    Icon: Award,
    title: "Premium Components",
    desc: "Trusted branded motor, gear system and electricals from leading Indian and global brands.",
  },
  {
    Icon: ShieldCheck,
    title: "Reliable Performance",
    desc: "Trial-run tested with safety verification for dependable performance project after project.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected",
    desc: "Every LRC Series machine is personally inspected and quality signed off before dispatch.",
  },
  {
    Icon: Flag,
    title: "Made in India",
    desc: "Designed and manufactured in India for Indian construction site conditions.",
  },
];

const TECH_HIGHLIGHTS = [
  {
    Icon: Gauge,
    title: "High Speed Cutting",
    desc: "2860 RPM motor delivers rapid cutting cycles — up to 2.5 tonnes per hour of rebar throughput.",
  },
  {
    Icon: Disc,
    title: "Heavy Duty Blades",
    desc: "Hardened, wear-resistant cutting blade holds tight tolerances through years of continuous operation.",
  },
  {
    Icon: Zap,
    title: "Industrial Motor",
    desc: "Premium copper winding motor with IP44 / IP54 / IP55 protection ratings for site-grade reliability.",
  },
  {
    Icon: Wrench,
    title: "Low Maintenance",
    desc: "Simple mechanical design with easy access to service points keeps operating costs low.",
  },
  {
    Icon: Target,
    title: "Accurate Performance",
    desc: "Precision-machined cutting mechanism delivers clean, straight and repeatable rebar cuts.",
  },
  {
    Icon: BadgeCheck,
    title: "Long Service Life",
    desc: "Premium materials, powder-coated corrosion protection and quality components ensure years of dependable use.",
  },
];

const FAQS = [
  {
    q: "What is the maximum bar diameter that can be cut?",
    a: "The LIFTILE LRC Series can cut TMT bars from 6 mm up to 42 mm.\n\nLRC40 handles up to 32 mm TMT bar. LRC50 handles up to 42 mm TMT bar. Both offer a minimum cutting diameter of 6 mm.",
  },
  {
    q: "How long does the cutting blade last?",
    a: "The LRC Series uses a hardened, high-strength cutting blade designed for long service life. Actual blade lifespan depends on the volume, diameter and grade of bars being cut, plus routine maintenance and lubrication practices.\n\nGenuine replacement blades are available through LIFTILE spare parts support.",
  },
  {
    q: "What maintenance does the LRC Series require?",
    a: "The LRC Series uses a simple, low-maintenance mechanical design. Routine inspection of the motor, gear system lubrication, cutting blade, electrical wiring and safety systems is all that is typically required for reliable long-term operation.",
  },
  {
    q: "What motor does the LRC Series use?",
    a: "The LRC Series uses a premium industrial-grade copper winding motor with IP44 / IP54 / IP55 protection ratings.\n\nLRC40 is equipped with a 5 HP motor and LRC50 with a 6.5 HP motor. Both operate at 2860 RPM for high-speed cutting performance.",
  },
  {
    q: "How is the LRC Series installed on site?",
    a: "The LRC Series is a compact, self-contained machine. It only requires a level surface, a standard three-phase power supply and basic setup. LIFTILE provides installation guidance and commissioning support on request.",
  },
  {
    q: "Are spare parts easily available?",
    a: "Yes.\n\nLIFTILE maintains ready inventory of genuine LRC Series spare parts including cutting blades, motor components, electricals and gear assembly items. Contact our spares team to source what you need for your machine.",
  },
  {
    q: "Does LIFTILE provide a warranty on the Bar Cutting Machine?",
    a: "Yes.\n\nEvery LIFTILE LRC Series machine is backed by our standard manufacturer's warranty on structural components, motor and gear system. Contact our sales team for the current warranty terms applicable to your project and region.",
  },
  {
    q: "Is service and support available across India?",
    a: "Yes.\n\nLIFTILE provides installation guidance, spare parts, technical assistance and after-sales service across India through our nationwide support network.",
  },
];

const RELATED_PRODUCTS = [
  {
    slug: "bar-bending-machine",
    name: "Bar Bending Machine",
    desc: "LRB Series heavy-duty rebar bender — the perfect on-site companion to the LRC cutter for a complete rebar prep pipeline.",
    image: "/product-bar-bending.jpg",
  },
  {
    slug: "monkey-lift",
    name: "Monkey Lift",
    desc: "Compact LML250 mini material lifter — ideal for moving cut rebar and materials to upper floors on tight sites.",
    image: "/product-monkey-lift.jpg",
  },
  {
    slug: "conventional-tower-hoist",
    name: "Conventional Tower Hoist",
    desc: "LTC Series wire-rope tower hoist for dependable material lifting up to 65 m on residential and industrial projects.",
    image: "/conventional-tower-hoist-site-2.jpeg",
  },
];

const CTA_BADGES = [
  "Automatic Operation",
  "Heavy Duty Construction",
  "Premium Components",
  "Made in India",
];

/* ---------- Blueprint SVG for Download Brochure ---------- */
const LRC_BLUEPRINT_SVG = (
  <svg
    aria-hidden
    viewBox="0 0 400 400"
    className="absolute right-0 top-1/2 -translate-y-1/2 w-[280px] md:w-[360px] h-auto opacity-[0.14] pointer-events-none"
    fill="none"
    stroke="#FF6B1A"
    strokeWidth="1.2"
  >
    {/* Machine base platform */}
    <rect x="80" y="220" width="240" height="30" strokeDasharray="0" />
    <line x1="100" y1="250" x2="100" y2="330" />
    <line x1="300" y1="250" x2="300" y2="330" />
    <line x1="90" y1="330" x2="310" y2="330" />
    <line x1="60" y1="360" x2="340" y2="360" strokeDasharray="4 4" opacity="0.6" />

    {/* Motor housing (left) */}
    <rect x="90" y="150" width="70" height="70" />
    <circle cx="125" cy="185" r="14" />
    {/* Motor cooling fins */}
    {[0, 1, 2, 3].map((i) => (
      <line
        key={i}
        x1={95 + i * 15}
        y1="155"
        x2={95 + i * 15}
        y2="180"
        opacity="0.6"
      />
    ))}
    {/* Drive belt / shaft coupling */}
    <line x1="160" y1="185" x2="210" y2="185" strokeWidth="2" />
    <line x1="160" y1="195" x2="210" y2="195" strokeDasharray="3 3" opacity="0.6" />

    {/* Gear housing (middle) */}
    <rect x="210" y="165" width="45" height="55" />

    {/* Cutting head (right) — with circular blade */}
    <circle cx="290" cy="192" r="34" strokeWidth="1.6" />
    <circle cx="290" cy="192" r="26" strokeDasharray="3 3" opacity="0.7" />
    <circle cx="290" cy="192" r="6" />
    {/* Blade teeth */}
    {Array.from({ length: 16 }).map((_, i) => {
      const a = (i * Math.PI * 2) / 16;
      const x1 = 290 + Math.cos(a) * 34;
      const y1 = 192 + Math.sin(a) * 34;
      const x2 = 290 + Math.cos(a) * 40;
      const y2 = 192 + Math.sin(a) * 40;
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} opacity="0.55" />;
    })}

    {/* Rebar feed line */}
    <line
      x1="200"
      y1="192"
      x2="290"
      y2="192"
      strokeDasharray="4 3"
      strokeWidth="2"
    />
    {/* Bar being cut — right side of blade */}
    <line x1="290" y1="192" x2="345" y2="192" strokeWidth="3" />
    {/* Cutting indicator (spark) */}
    <line x1="290" y1="192" x2="300" y2="175" strokeDasharray="2 2" opacity="0.6" />
    <line x1="290" y1="192" x2="278" y2="175" strokeDasharray="2 2" opacity="0.6" />

    {/* Dimension label */}
    <text
      x="245"
      y="145"
      fill="#FF6B1A"
      fontSize="10"
      fontFamily="Montserrat"
      opacity="0.7"
    >
      Ø 42mm
    </text>
    {/* Component labels */}
    <text
      x="115"
      y="145"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      MOTOR
    </text>
    <text
      x="215"
      y="160"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      GEAR
    </text>
    <text
      x="270"
      y="255"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      CUTTING BLADE
    </text>
  </svg>
);

export default function BarCuttingMachinePage() {
  return (
    <>
      <Navbar />
      <ProductBreadcrumb productName="Bar Cutting Machine (LRC Series)" />

      {/* ─── SECTION 1: HERO ─── */}
      <ProductHero
        testId="lrc-hero"
        backgroundImage={HERO_IMAGE}
        eyebrow="LRC Series"
        title={
          <>
            LIFTILE Bar
            <br />
            Cutting <span className="text-liftile-orange">Machine</span>
          </>
        }
        subtitle="Engineered for fast, precise and efficient reinforcement steel cutting. Built with a heavy-duty structure and premium components to deliver reliable performance for demanding construction projects."
        models={Object.keys(MODELS)}
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Download Brochure" }}
        specs={HERO_SPECS}
        badges={HERO_BADGES}
      />

      {/* ─── SECTION 2: GALLERY ─── */}
      <ProductGallery
        testId="lrc-gallery"
        heading={
          <>
            LRC Series <span className="text-liftile-orange">In Action</span>
          </>
        }
        subtitle="A closer look at the LIFTILE LRC Series Bar Cutting Machine — from precision fabrication at our factory to real construction sites across India."
        items={GALLERY}
        seriesLabel="LRC Series"
        imageAlt="LIFTILE LRC Series Bar Cutting Machine"
      />

      {/* ─── SECTION 3: QUICK SPECS (split layout with 2 model tabs) ─── */}
      <QuickSpecsTabs
        testId="lrc-quick-specs"
        heading={
          <>
            Quick <span className="text-liftile-orange">Specifications</span>
          </>
        }
        subtitle="The essentials — with a model selector to switch between LRC40 and LRC50 configurations."
        quickSpecs={QUICK_SPECS}
        models={MODELS}
        defaultModel="LRC40"
        footerNote={null}
      />

      {/* ─── SECTION 4: KEY FEATURES ─── */}
      <KeyFeatures
        testId="lrc-features"
        heading={
          <>
            Built for Speed.{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">
              Engineered for Precision.
            </span>
          </>
        }
        subtitle="Every LIFTILE LRC Series Bar Cutting Machine is engineered with premium branded components and rigorous quality inspections to deliver dependable performance on demanding construction sites."
        items={FEATURES}
      />

      {/* ─── SECTION 5: APPLICATIONS ─── */}
      <Applications
        testId="lrc-applications"
        heading={
          <>
            Ideal for Every{" "}
            <span className="text-liftile-orange">Construction Project</span>
          </>
        }
        subtitle="From residential builds to bridge construction and precast industries — the LRC Series is engineered for fast, accurate rebar cutting across every construction environment."
        items={APPLICATIONS}
        variant="dark"
        gridCols="lg:grid-cols-4"
      />

      {/* ─── SECTION 6: WHY CHOOSE LIFTILE ─── */}
      <WhyChooseLiftile
        testId="lrc-why-choose"
        image="/product-bar-cutting.jpg"
        imageAlt="LIFTILE LRC Series Bar Cutting Machine — heavy-duty rebar cutting"
        imageCaption="LRC Series — high-speed, precision rebar cutting, built for continuous site operation."
        floatingBadge={{ top: "Up to", bottom: "42 mm TMT" }}
        eyebrow="Why LRC Series"
        heading={
          <>
            Built for Heavy Duty{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">Rebar Operations</span>
          </>
        }
        subtitle="The LRC Series combines heavy-duty construction, high-speed cutting performance and premium branded components to deliver dependable rebar cutting on demanding construction projects."
        trustCards={TRUST_CARDS}
      />

      {/* ─── SECTION 7: TECHNICAL HIGHLIGHTS ─── */}
      <QualitySafetyCommitment
        testId="lrc-tech-highlights"
        eyebrow="Technical Highlights"
        heading={
          <>
            Precision Engineering.{" "}
            <span className="text-liftile-orange">Industrial Grade Build.</span>
          </>
        }
        subtitle="A closer look at the engineering foundations that make the LRC Series a fast, accurate and reliable rebar cutting machine for modern construction sites."
        items={TECH_HIGHLIGHTS}
      />

      {/* ─── SECTION 8: DOWNLOAD BROCHURE ─── */}
      <DownloadBrochure
        testId="lrc-brochure"
        heading={
          <>
            Download Product{" "}
            <span className="text-liftile-orange">Brochure</span>
          </>
        }
        subtitle="Download the complete LRC Series technical catalogue including specifications, drawings, features and product information for LRC40 and LRC50 configurations."
        blueprintSvg={LRC_BLUEPRINT_SVG}
      />

      {/* ─── SECTION 9: FAQ ─── */}
      <PremiumFAQ
        testId="lrc-faq"
        heading={
          <>
            Frequently Asked{" "}
            <span className="text-liftile-orange">Questions</span>
          </>
        }
        subtitle="Everything builders, contractors and rebar fabricators most often ask us about the LIFTILE LRC Series Bar Cutting Machine."
        items={FAQS}
      />

      {/* ─── SECTION 10: RELATED PRODUCTS ─── */}
      <RelatedProducts
        testId="lrc-related"
        heading={
          <>
            Explore More{" "}
            <span className="text-liftile-orange">LIFTILE Solutions</span>
          </>
        }
        items={RELATED_PRODUCTS}
      />

      {/* ─── SECTION 11: FINAL CTA ─── */}
      <ProductFinalCTA
        testId="lrc-product-cta"
        backgroundImage="/project-pandesara-surat.png"
        backgroundAlt="LIFTILE LRC Series Bar Cutting Machine on a live construction site"
        heading={
          <>
            Power Through{" "}
            <span className="text-liftile-orange">Every Cut.</span>
          </>
        }
        subtitle="The LIFTILE LRC Series delivers powerful, accurate and dependable reinforcement steel cutting for modern construction projects."
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Contact Sales Team", href: "/contact" }}
        trustBadges={CTA_BADGES}
      />

      <Footer />
    </>
  );
}
